import React, { FunctionComponent, useMemo } from 'react';
import { APIReferenceMember, isMathOperationMember } from '@modules/client';
import { SecurityTag } from '@modules/ui/tags/types';
import usePagePath from '@modules/eventStream/hooks/usePagePath';
import {
  getCharacteristicKey,
  getParams,
  isEvent,
  isProperty,
  getClassRowName,
} from '../../utils/characteristicsRowUtils';
import { CharacteristicsRowTypes } from '../../types';
import Iterator from './Iterator';
import { APIReferenceDescriptionProps } from '../../sharedUI/components/APIReferenceDescription';
import { getUniqueMemberTitleHashId } from '../../utils/apiReferencePageNavUtils';
import useCharacteristicsTableContainerStyles from './CharacteristicsTableContainer.styles';

export interface CharacteristicsIteratorProps {
  rows: Array<CharacteristicsRowTypes & APIReferenceDescriptionProps>;
}

export const getIteratorRows = (
  row: CharacteristicsRowTypes,
  pageData?: { title: string },
  isSameNameAsPrevMember?: boolean,
) => {
  let newType;
  let securities: SecurityTag[] | null = null;
  const { deprecationMessage, security, threadSafety, description, codeSamples, tags } = row;
  const paramsList = getParams(row);
  const trimmedName = pageData?.title ?? getClassRowName(row);
  const id = getUniqueMemberTitleHashId(
    // FIXME: DatatypeCharacteristicRowTypes needs to be replaced by
    // more generalized APIReferenceMember type
    row as unknown as APIReferenceMember,
    !!isSameNameAsPrevMember,
  );

  if (!security) {
    securities = null;
  } else if (Array.isArray(security)) {
    securities = security;
  } else if (security.read !== null || security.write !== null) {
    securities = [];

    // Check if security.read and security.write are equal to avoid duplication
    if (security.read !== null && security.read === security.write) {
      securities.push(security.read);
    } else {
      if (security.read !== null) {
        securities.push(security.read);
      }
      if (security.write !== null) {
        securities.push(security.write);
      }
    }
  }

  if (!isEvent(row)) {
    if (isProperty(row)) {
      newType = row.type;
    } else if (row.returns !== null && row.returns !== undefined && row.returns.length > 0) {
      newType = row?.returns[0].type;
    }
  }

  return (
    <Iterator
      key={getCharacteristicKey(trimmedName, paramsList)}
      name={trimmedName}
      type={newType}
      tags={tags}
      securities={securities}
      threadSafety={threadSafety}
      deprecationMessage={deprecationMessage}
      description={description}
      row={row}
      codeSamples={codeSamples}
      renderAsPage={!!pageData}
      id={id}
    />
  );
};

const CharacteristicsIterator: FunctionComponent<
  React.PropsWithChildren<CharacteristicsIteratorProps>
> = ({ rows }) => {
  const {
    classes: { members },
  } = useCharacteristicsTableContainerStyles();
  const path = usePagePath();
  const rowsElement = useMemo(
    () =>
      path
        ? rows.map((row, i) => {
            const prevMember = rows[i - 1] as APIReferenceMember;
            const isSameNameAsPrevMember =
              isMathOperationMember(row as APIReferenceMember) ||
              !prevMember ||
              isMathOperationMember(prevMember)
                ? false
                : row.name === prevMember.name;
            return getIteratorRows(row, undefined, isSameNameAsPrevMember);
          })
        : null,
    [path, rows],
  );

  return <div className={members}>{rowsElement}</div>;
};

export default CharacteristicsIterator;
